'use client'

import React from 'react'
import { usePathname, useRouter } from 'next/navigation'

import { AGE_GATE_COOKIE, AGE_GATE_UNLOCK_EVENT } from '@/constants/ageGate'

const REMEMBER_MAX_AGE_SECONDS = 60 * 60 * 24 * 30

type Choice = 'yes' | 'no'
type AgeGateProps = {
  initialChoice?: Choice | null
}

function setAgeGateCookie(choice: Choice, remember: boolean) {
  const baseCookie = `${AGE_GATE_COOKIE}=${choice}; path=/; SameSite=Lax`

  if (!remember) {
    document.cookie = baseCookie
    return
  }

  document.cookie = `${baseCookie}; Max-Age=${REMEMBER_MAX_AGE_SECONDS}`
}

function AgeGateInner({ initialChoice = null }: AgeGateProps) {
  const router = useRouter()
  const pathname = usePathname()
  const [remember, setRemember] = React.useState(false)
  const [isDismissed, setIsDismissed] = React.useState(initialChoice === 'yes')

  React.useEffect(() => {
    const handleUnlock = () => {
      setIsDismissed(true)
    }

    window.addEventListener(AGE_GATE_UNLOCK_EVENT, handleUnlock)
    return () => {
      window.removeEventListener(AGE_GATE_UNLOCK_EVENT, handleUnlock)
    }
  }, [])

  const handleChoice = (choice: Choice) => {
    if (choice === 'yes') {
      setAgeGateCookie(choice, remember)
      window.dispatchEvent(new Event(AGE_GATE_UNLOCK_EVENT))
      return
    }

    setAgeGateCookie(choice, remember)
    router.push('/underage-drinking')
  }

  if (isDismissed || pathname === '/underage-drinking') return null

  return (
    <section
      aria-labelledby="age-gate-title"
      aria-modal="true"
      className="fixed inset-0 isolate z-[10000] grid place-items-center bg-black/80 p-8 backdrop-blur-md"
      role="dialog"
    >
      <div className="w-full max-w-2xl border-2 border-primary-light/80 bg-zinc-950 p-8 text-center text-white shadow-2xl md:p-12">
        <p className="m-0 text-xs font-bold tracking-[0.28em] text-primary-light uppercase">
          Åldersverifiering
        </p>
        <h1
          className="font-display m-0 mt-6 text-5xl leading-[0.95] font-normal tracking-wide text-white uppercase md:text-6xl"
          id="age-gate-title"
        >
          Har du fyllt 18?
        </h1>
        <p className="mx-auto mt-6 text-zinc-300">
          Bekräfta att du har uppnått laglig ålder för att dricka alkohol innan du går vidare.
        </p>

        <div className="my-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <button
            className="age-type-action focus-visible:ring-primary/50 min-h-20 border-2 border-green-500 bg-green-600 text-white shadow-none transition duration-150 ease-out hover:-translate-y-0.5 hover:border-green-400 hover:bg-green-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-500"
            onClick={() => handleChoice('yes')}
            type="button"
          >
            Ja
          </button>
          <button
            className="age-type-action focus-visible:ring-primary/50 min-h-20 border-2 border-white/35 bg-black text-white transition duration-150 ease-out hover:-translate-y-0.5 hover:border-white hover:bg-zinc-950 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/60"
            onClick={() => handleChoice('no')}
            type="button"
          >
            Nej
          </button>
        </div>

        <label className="inline-flex cursor-pointer items-center gap-3 text-sm text-zinc-300">
          <input
            className="h-4 w-4 cursor-pointer rounded border border-primary-light/70 bg-black accent-primary-light"
            checked={remember}
            onChange={(event) => setRemember(event.target.checked)}
            type="checkbox"
          />
          <span className="text-balance">Kom ihåg i 30 dagar</span>
        </label>
      </div>
    </section>
  )
}

export function AgeGate(props: AgeGateProps) {
  return <AgeGateInner {...props} />
}
